import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { tokenUtils } from '../services/api';

const AuthContext = createContext();

const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  isAdmin: false,
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        isAdmin: action.payload.isAdmin || false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        isAdmin: false,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    // Check for existing authentication on app load with token validation
    console.log('🔍 AuthContext: Checking for existing authentication...');
    
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    const isAdmin = localStorage.getItem('isAdmin') === 'true';

    if (token && user) {
      // Validate token before using it
      if (tokenUtils.isValidToken()) {
        console.log('✅ AuthContext: Valid token found, restoring session');
        const expiration = tokenUtils.getTokenExpiration();
        console.log('🕐 Token expires at:', expiration);
        
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            token,
            user: JSON.parse(user),
            isAdmin,
          },
        });
      } else {
        console.log('🔒 AuthContext: Found expired token, clearing session');
        // Clear expired token data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('isAdmin');
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    } else {
      console.log('ℹ️ AuthContext: No authentication data found');
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  const login = (token, user, isAdmin = false) => {
    console.log('🔐 AuthContext: Logging in user:', user.username || user.name);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('isAdmin', isAdmin.toString());
    
    dispatch({
      type: 'LOGIN_SUCCESS',
      payload: { token, user, isAdmin },
    });
  };

  const logout = () => {
    console.log('🚪 AuthContext: Logging out user');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isAdmin');
    
    dispatch({ type: 'LOGOUT' });
  };

  const value = {
    ...state,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
